// This is a simplified SessionPage for the passkey architecture
// It receives cohortId and manages the session recording flow
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useReadContract, useReadContracts } from 'wagmi';
import { COHORT_ABI } from '../contract/abi';
import { RecordSession } from './RecordSession';
import { RelayerBanner } from './RelayerBanner';
import { DistributionPreview, DEFAULT_MULTIPLIERS, useDistribution } from './DistributionPreview';
import { SessionCard } from './SessionCard';
import { SessionTable } from './SessionTable';
import { signFinalize, signInvalidateSession, signAddSigner } from '../utils/signing';
import { relayFinalize, relayInvalidateSession, relayRequestSigner, relayAddSigner, isRelayerConfigured } from '../services/relayer';
import { usePublicClient } from 'wagmi';
import { parseAbiItem } from 'viem';
import { AddressInput } from './AddressInput';
import { CopyableAddress } from './CopyableAddress';

interface SessionPageProps {
  cohortId: `0x${string}`;
  cohortTitle: string;
  passkeyAddress: `0x${string}` | null;
}

export function SessionPage({ cohortId, cohortTitle, passkeyAddress }: SessionPageProps) {
  const navigate = useNavigate();
  const [showRecordSession, setShowRecordSession] = useState(false);
  const [multipliers, setMultipliers] = useState(DEFAULT_MULTIPLIERS);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  const [isFinalizePending, setIsFinalizePending] = useState(false);
  const [invalidatingSession, setInvalidatingSession] = useState<number | null>(null);
  const [isRequestingAccess, setIsRequestingAccess] = useState(false);
  const [pendingRequests, setPendingRequests] = useState<`0x${string}`[]>([]);
  const [approvingAddress, setApprovingAddress] = useState<`0x${string}` | null>(null);
  const [validatedSignerAddress, setValidatedSignerAddress] = useState<string | null>(null);

  const contractAddress = import.meta.env.VITE_CONTRACT_ADDRESS as `0x${string}`;
  const publicClient = usePublicClient();

  // Read contract state - always refetch on mount to avoid stale data after navigation
  const queryOpts = { refetchOnMount: 'always' as const, staleTime: 0 };

  const { data: signers, refetch: refetchSigners } = useReadContract({
    address: contractAddress,
    abi: COHORT_ABI,
    functionName: 'getSigners',
    args: [cohortId],
    query: queryOpts,
  });

  const { data: sessionCount, refetch: refetchSessionCount } = useReadContract({
    address: contractAddress,
    abi: COHORT_ABI,
    functionName: 'sessionCount',
    args: [cohortId],
    query: queryOpts,
  });

  const { data: isFinalized, refetch: refetchFinalized } = useReadContract({
    address: contractAddress,
    abi: COHORT_ABI,
    functionName: 'finalized',
    args: [cohortId],
    query: queryOpts,
  });

  // Get participant IDs for this cohort
  const { data: participantIds, refetch: refetchParticipants } = useReadContract({
    address: contractAddress,
    abi: COHORT_ABI,
    functionName: 'getParticipantIds',
    args: [cohortId],
    query: queryOpts,
  });

  // Get signer nonce for addSigner operations
  const { data: signerNonce, refetch: refetchSignerNonce } = useReadContract({
    address: contractAddress,
    abi: COHORT_ABI,
    functionName: 'signerNonce',
    args: [cohortId],
    query: queryOpts,
  });

  const sessionCountNum = sessionCount ? Number(sessionCount) : 0;

  // Build contract read configs for session importance
  const sessionImportanceConfigs = useMemo(() => {
    if (sessionCountNum === 0) return [];
    return Array.from({ length: sessionCountNum }, (_, i) => ({
      address: contractAddress,
      abi: COHORT_ABI,
      functionName: 'sessionImportance' as const,
      args: [cohortId, BigInt(i)] as const,
    }));
  }, [sessionCountNum, cohortId, contractAddress]);

  // Build contract read configs for session invalidated status
  const sessionInvalidatedConfigs = useMemo(() => {
    if (sessionCountNum === 0) return [];
    return Array.from({ length: sessionCountNum }, (_, i) => ({
      address: contractAddress,
      abi: COHORT_ABI,
      functionName: 'sessionInvalidated' as const,
      args: [cohortId, BigInt(i)] as const,
    }));
  }, [sessionCountNum, cohortId, contractAddress]);

  // Build contract read configs for session timestamps
  const sessionTimestampConfigs = useMemo(() => {
    if (sessionCountNum === 0) return [];
    return Array.from({ length: sessionCountNum }, (_, i) => ({
      address: contractAddress,
      abi: COHORT_ABI,
      functionName: 'sessionTimestamp' as const,
      args: [cohortId, BigInt(i)] as const,
    }));
  }, [sessionCountNum, cohortId, contractAddress]);

  // Build contract read configs for session points (for each session x participant combination)
  const sessionPointsConfigs = useMemo(() => {
    if (sessionCountNum === 0 || !participantIds || participantIds.length === 0) return [];
    const configs = [];
    for (let sessionIdx = 0; sessionIdx < sessionCountNum; sessionIdx++) {
      for (const participantId of participantIds) {
        configs.push({
          address: contractAddress,
          abi: COHORT_ABI,
          functionName: 'sessionPoints' as const,
          args: [cohortId, BigInt(sessionIdx), participantId] as const,
        });
      }
    }
    return configs;
  }, [sessionCountNum, cohortId, contractAddress, participantIds]);

  // Fetch session importance values
  const { data: importanceResults, refetch: refetchImportance } = useReadContracts({
    contracts: sessionImportanceConfigs,
    query: { enabled: sessionCountNum > 0 },
  });

  // Fetch session invalidated status
  const { data: invalidatedResults, refetch: refetchInvalidated } = useReadContracts({
    contracts: sessionInvalidatedConfigs,
    query: { enabled: sessionCountNum > 0 },
  });

  // Fetch session timestamps
  const { data: timestampResults, refetch: refetchTimestamps } = useReadContracts({
    contracts: sessionTimestampConfigs,
    query: { enabled: sessionCountNum > 0 },
  });

  // Fetch session points for participant details
  const { data: sessionPointsResults, refetch: refetchSessionPoints } = useReadContracts({
    contracts: sessionPointsConfigs,
    query: { enabled: sessionPointsConfigs.length > 0 },
  });

  const isSigner = passkeyAddress && signers && signers.some(s => s.toLowerCase() === passkeyAddress.toLowerCase());
  const isNewCohort = !signers || signers.length === 0;

  // Build contract read configs for participant names (look up from on-chain registry)
  const participantNameConfigs = useMemo(() => {
    if (!participantIds || participantIds.length === 0) return [];
    return participantIds.map((id) => ({
      address: contractAddress,
      abi: COHORT_ABI,
      functionName: 'names' as const,
      args: [id] as const,
    }));
  }, [participantIds, contractAddress]);

  // Fetch participant names from chain
  const { data: participantNameResults } = useReadContracts({
    contracts: participantNameConfigs,
  });

  // Extract participant names from results
  const participantNames = useMemo(() => {
    if (!participantNameResults || !participantIds) return [];
    return participantNameResults.map((result, index) => {
      const name = result?.result as string | undefined;
      return name || `Participant ${index + 1}`;
    });
  }, [participantNameResults, participantIds]);

  // Combine into sessions array with participant data
  const sessions = useMemo(() => {
    if (!importanceResults || sessionCountNum === 0) return [];

    const numParticipants = participantIds?.length || 0;

    return Array.from({ length: sessionCountNum }, (_, sessionIdx) => {
      const importance = importanceResults[sessionIdx]?.result as number | undefined;
      const invalidated = invalidatedResults?.[sessionIdx]?.result as boolean | undefined;
      const timestamp = timestampResults?.[sessionIdx]?.result as bigint | undefined;

      // Get participant points for this session
      const sessionParticipants: { name: string; points: number; colorIndex: number }[] = [];
      if (sessionPointsResults && participantIds && numParticipants > 0) {
        for (let pIdx = 0; pIdx < numParticipants; pIdx++) {
          const resultIdx = sessionIdx * numParticipants + pIdx;
          const points = sessionPointsResults[resultIdx]?.result as bigint | undefined;
          const pointsNum = points ? Number(points) : 0;
          const name = participantNames[pIdx] || `Participant ${pIdx + 1}`;
          sessionParticipants.push({
            name,
            points: pointsNum,
            colorIndex: pIdx, // Use global participant index for consistent colors
          });
        }
      }

      return {
        sessionId: sessionIdx,
        importance: importance ?? 1,
        invalidated: invalidated ?? false,
        timestamp: timestamp ? Number(timestamp) : undefined,
        participants: sessionParticipants,
      };
    });
  }, [importanceResults, invalidatedResults, timestampResults, sessionCountNum, sessionPointsResults, participantIds, participantNames]);

  const refetchAll = () => {
    refetchSigners();
    refetchSessionCount();
    refetchFinalized();
    refetchParticipants();
    refetchImportance();
    refetchInvalidated();
    refetchTimestamps();
    refetchSignerNonce();
    refetchSessionPoints();
    refetchDistribution();
  };

  // Use shared distribution hook
  const { distribution, refetch: refetchDistribution } = useDistribution(
    cohortId,
    contractAddress,
    multipliers,
    participantNames,
    sessionCountNum > 0
  );

  const handleFinalize = async () => {
    if (!isRelayerConfigured() || !publicClient) return;

    setIsFinalizePending(true);
    try {
      const sig = await signFinalize(cohortId);
      const result = await relayFinalize({
        cohortId,
        v: sig.v,
        r: sig.r,
        s: sig.s,
      });

      // Wait for transaction to be mined before refetching
      if (result.txHash) {
        await publicClient.waitForTransactionReceipt({
          hash: result.txHash as `0x${string}`
        });
      }
      refetchAll();
    } catch (err) {
      console.error('Failed to finalize:', err);
    } finally {
      setIsFinalizePending(false);
    }
  };

  const handleInvalidateSession = async (sessionId: number) => {
    if (!isRelayerConfigured() || !publicClient) return;

    setInvalidatingSession(sessionId);
    try {
      const sig = await signInvalidateSession(cohortId, BigInt(sessionId));
      const result = await relayInvalidateSession({
        cohortId,
        sessionId: BigInt(sessionId),
        v: sig.v,
        r: sig.r,
        s: sig.s,
      });

      // Wait for transaction to be mined before refetching
      if (result.txHash) {
        await publicClient.waitForTransactionReceipt({
          hash: result.txHash as `0x${string}`
        });
      }
      refetchAll();
    } catch (err) {
      console.error('Failed to invalidate session:', err);
    } finally {
      setInvalidatingSession(null);
    }
  };

  // Fetch pending signer requests from recent events
  const fetchPendingRequests = async () => {
    if (!publicClient || !isSigner) return;

    try {
      // Look back ~9000 blocks (~5 hours on Optimism) - free tier RPCs limit to 10k blocks
      const currentBlock = await publicClient.getBlockNumber();
      const fromBlock = currentBlock > 9000n ? currentBlock - 9000n : 0n;

      const logs = await publicClient.getLogs({
        address: contractAddress,
        event: parseAbiItem('event SignerRequested(bytes32 indexed cohortId, address indexed requester)'),
        args: { cohortId },
        fromBlock,
        toBlock: 'latest',
      });

      // Filter out addresses that are already signers
      const currentSigners = new Set((signers || []).map(s => s.toLowerCase()));
      const requests = logs
        .map(log => log.args.requester as `0x${string}`)
        .filter(addr => addr && !currentSigners.has(addr.toLowerCase()));

      // Deduplicate
      const unique = [...new Set(requests)];
      setPendingRequests(unique);
    } catch (err) {
      console.error('Failed to fetch pending requests:', err);
    }
  };

  // Fetch pending requests when component loads (for signers)
  useEffect(() => {
    if (isSigner && !isFinalized) {
      fetchPendingRequests();
    }
  }, [isSigner, signers, isFinalized]);

  // Handle requesting to be added as a signer
  const handleRequestAccess = async () => {
    if (!isRelayerConfigured() || !passkeyAddress) return;

    setIsRequestingAccess(true);
    try {
      await relayRequestSigner({
        cohortId,
        requester: passkeyAddress,
      });
      alert('Access request submitted! An existing signer can now approve you.');
    } catch (err) {
      console.error('Failed to request access:', err);
    } finally {
      setIsRequestingAccess(false);
    }
  };

  // Handle approving a signer request
  const handleApproveSigner = async (newSigner: `0x${string}`) => {
    if (!isRelayerConfigured()) return;
    if (signerNonce === undefined) {
      console.error('Signer nonce not loaded');
      return;
    }

    setApprovingAddress(newSigner);
    try {
      const sig = await signAddSigner(cohortId, newSigner, signerNonce);
      await relayAddSigner({
        cohortId,
        newSigner,
        v: sig.v,
        r: sig.r,
        s: sig.s,
      });
      refetchAll();
      // Remove from pending requests
      setPendingRequests(prev => prev.filter(addr => addr.toLowerCase() !== newSigner.toLowerCase()));
    } catch (err) {
      console.error('Failed to approve signer:', err);
    } finally {
      setApprovingAddress(null);
    }
  };

  // Handle adding signer via manual input
  const handleAddManualSigner = async () => {
    if (!validatedSignerAddress) {
      alert('Please enter a valid Ethereum address or ENS name');
      return;
    }
    await handleApproveSigner(validatedSignerAddress as `0x${string}`);
    setValidatedSignerAddress(null);
  };

  if (showRecordSession && (isSigner || isNewCohort) && !isFinalized) {
    return (
      <RecordSession
        cohortId={cohortId}
        cohortTitle={cohortTitle}
        onBack={() => {
          setShowRecordSession(false);
          refetchAll();
        }}
        onSessionRecorded={refetchAll}
      />
    );
  }


  return (
    <div className="container">
      <RelayerBanner />

      <div className="card">
        <div className="card-header">
          <div>
            <h1>{cohortTitle}</h1>
            <p className="text-muted" style={{ fontSize: '0.875rem', margin: 0 }}>
              Cohort ID: {cohortId.slice(0, 10)}...{cohortId.slice(-8)}
            </p>
          </div>
          <div className="flex items-center gap-md">
            {isFinalized && <span className="badge badge-finalized">Finalized</span>}
            {isNewCohort && <span className="badge badge-new">New</span>}
            {!isFinalized && !isNewCohort && <span className="badge badge-active">Active</span>}
          </div>
        </div>

        {isNewCohort && (
          <div className="message message-info">
            This is a new cohort. Record the first session to initialize it on-chain.
          </div>
        )}

        {!passkeyAddress && (
          <div className="message message-info">
            <a href="#" onClick={(e) => { e.preventDefault(); navigate('/auth', { state: { from: `/${cohortId}` } }); }}>
              Sign in with passkey
            </a> to record sessions or manage this cohort.
          </div>
        )}

        {passkeyAddress && !isSigner && !isNewCohort && (
          <div className="message message-warning">
            You are not a signer for this cohort. You can view but not modify it.
          </div>
        )}

        {/* Action buttons - constrained width on desktop */}
        <div className="action-buttons">
          {(isSigner || isNewCohort) && !isFinalized && (
            <button
              onClick={() => setShowRecordSession(true)}
              className="btn-primary action-btn-primary"
            >
              + New Session
            </button>
          )}

          {isFinalized && (
            <button
              onClick={() => navigate(`/${cohortId}/distribute`)}
              className="btn-primary action-btn-primary"
            >
              Distribute Tokens
            </button>
          )}
        </div>

        <div className="mt-lg">
          <h2>Cohort Information</h2>
          <div className="flex gap-lg" style={{ marginTop: 'var(--space-md)' }}>
            <div>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: 'var(--space-xs)' }}>
                Signers
              </p>
              <p style={{ fontWeight: 600 }}>{signers?.length || 0}</p>
            </div>
            <div>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: 'var(--space-xs)' }}>
                Sessions
              </p>
              <p style={{ fontWeight: 600 }}>{sessionCountNum}</p>
            </div>
            <div>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: 'var(--space-xs)' }}>
                Participants
              </p>
              <p style={{ fontWeight: 600 }}>{participantIds?.length || 0}</p>
            </div>
          </div>
        </div>

        {sessions.length > 0 && (
          <div className="mt-lg">
            <h2>Sessions</h2>
            {/* Desktop: Table view */}
            <div className="sessions-table-desktop">
              <SessionTable
                sessions={sessions}
                isSigner={isSigner || false}
                isFinalized={isFinalized || false}
                invalidatingSession={invalidatingSession}
                onInvalidate={handleInvalidateSession}
              />
            </div>
            {/* Mobile: Card view */}
            <div className="sessions-list-mobile">
              {sessions.map((session) => (
                <SessionCard
                  key={session.sessionId}
                  sessionId={session.sessionId}
                  importance={session.importance}
                  invalidated={session.invalidated}
                  timestamp={session.timestamp}
                  participants={session.participants}
                  isSigner={isSigner || false}
                  isFinalized={isFinalized || false}
                  isInvalidating={invalidatingSession === session.sessionId}
                  onInvalidate={() => handleInvalidateSession(session.sessionId)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Distribution Preview with Pie Chart */}
        {distribution.length > 0 && (
          <div className="mt-lg">
            <DistributionPreview
              cohortId={cohortId}
              contractAddress={contractAddress}
              participantNames={participantNames}
              multipliers={multipliers}
              onMultipliersChange={setMultipliers}
              advancedSettingsOpen={advancedSettingsOpen}
              onAdvancedSettingsToggle={setAdvancedSettingsOpen}
              showResetButton={true}
            />
          </div>
        )}

      </div>

      {/* Signer Management - Separate Card */}
      {!isNewCohort && !isFinalized && (
        <div className="card" style={{ marginTop: 'var(--space-lg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
            <h2 style={{ margin: 0 }}>Signer Management</h2>
            {isSigner && (
              <button
                onClick={fetchPendingRequests}
                title="Refresh pending requests"
                style={{
                  background: 'transparent',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '0.25rem 0.5rem',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  color: 'var(--color-text-muted)',
                }}
              >
                ↻
              </button>
            )}
          </div>

          {/* Current signers list */}
          {signers && signers.length > 0 && (
            <div style={{ marginBottom: 'var(--space-md)' }}>
              <p style={{ fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                Current Signers ({signers.length})
              </p>
              <div className="flex" style={{ flexDirection: 'column', gap: 'var(--space-xs)' }}>
                {signers.map((addr) => {
                  const isSelf = passkeyAddress && addr.toLowerCase() === passkeyAddress.toLowerCase();
                  return (
                    <CopyableAddress
                      key={addr}
                      address={addr}
                      truncate={false}
                      showYou={!!isSelf}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Request access button for non-signers */}
          {!isSigner && (
            <div>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: 'var(--space-sm)' }}>
                Request to be added as a signer. Your address:
              </p>
              <div style={{ marginBottom: 'var(--space-sm)' }}>
                {passkeyAddress && <CopyableAddress address={passkeyAddress} truncate={false} showYou />}
              </div>
              <button
                onClick={handleRequestAccess}
                disabled={isRequestingAccess}
                className="btn-primary"
              >
                {isRequestingAccess ? 'Requesting...' : 'Request Access'}
              </button>
            </div>
          )}

          {/* Pending requests for signers */}
          {isSigner && pendingRequests.length > 0 && (
            <div style={{ marginBottom: 'var(--space-md)' }}>
              <p style={{ fontWeight: 600, marginBottom: 'var(--space-sm)' }}>Pending Requests</p>
              {pendingRequests.map((addr) => (
                <div key={addr} className="flex items-center gap-md" style={{ marginBottom: 'var(--space-sm)' }}>
                  <CopyableAddress address={addr} truncate={false} />
                  <button
                    onClick={() => handleApproveSigner(addr)}
                    disabled={approvingAddress === addr}
                    className="btn-primary btn-small"
                  >
                    {approvingAddress === addr ? 'Approving...' : 'Approve'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Manual add signer for signers */}
          {isSigner && (
            <div>
              <p style={{ fontWeight: 600, marginBottom: 'var(--space-sm)' }}>Add Signer Manually</p>
              <div className="flex gap-sm" style={{ alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <AddressInput
                    onAddressChange={(addr) => setValidatedSignerAddress(addr)}
                    placeholder="0x... or name.eth"
                    id="manual-signer-address"
                  />
                </div>
                <button
                  onClick={handleAddManualSigner}
                  disabled={!validatedSignerAddress || approvingAddress !== null}
                  className="btn-secondary"
                  style={{ marginTop: '2px' }}
                >
                  Add
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Finalize Section - Separate Card */}
      {isSigner && !isFinalized && sessionCountNum > 0 && (
        <div className="card" style={{ marginTop: 'var(--space-lg)' }}>
          <h2 style={{ marginBottom: 'var(--space-md)' }}>End of Cohort</h2>
          <div className="warning-box">
            <span className="warning-box-icon" aria-hidden="true">⚠️</span>
            <div className="warning-box-content">
              <strong>Finalizing is permanent and cannot be undone.</strong> Once finalized,
              no more sessions can be recorded, participants cannot be added or changed,
              and signers cannot be modified. Only token distribution will remain possible.
            </div>
          </div>
          <button
            onClick={() => {
              if (confirm('Are you sure you want to finalize this cohort? This action is irreversible.')) {
                handleFinalize();
              }
            }}
            disabled={isFinalizePending}
            className="btn-secondary"
          >
            {isFinalizePending ? 'Finalizing...' : 'Finalize Cohort'}
          </button>
        </div>
      )}

      {/* Change Cohort button at the bottom */}
      <div className="change-cohort-section">
        <button onClick={() => navigate('/')} className="btn-secondary btn-small">
          Change Cohort
        </button>
      </div>
    </div>
  );
}
